import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { NavLink } from "@/components/NavLink";
import { Hash, Dice1, Percent, Calculator, TrendingUp, ListOrdered } from "lucide-react";

const NumbersGeneratorSitemap = () => {
  const numberTools = [
    {
      category: "Random Number Generators",
      description: "Generate cryptographically secure random numbers for various purposes",
      tools: [
        { 
          name: "Basic Number Generator", 
          path: "/numbers", 
          description: "Generate random numbers within any range with customizable minimum and maximum values",
          icon: Hash 
        },
        { 
          name: "Decimal Generator", 
          path: "/numbers#decimal", 
          description: "Create random decimal numbers with specified precision up to 10 decimal places",
          icon: Percent 
        },
        { 
          name: "Lucky Draw Generator", 
          path: "/", 
          description: "Interactive spinning animation for random number selection and lucky draws",
          icon: TrendingUp 
        },
      ]
    },
    {
      category: "Gaming & Random Selection",
      description: "Tools for games, contests, and random selection events",
      tools: [
        { 
          name: "Dice Roller", 
          path: "/games#dice", 
          description: "Virtual dice roller with realistic 3D animation and multiple dice support",
          icon: Dice1 
        },
        { 
          name: "List Picker", 
          path: "/lists", 
          description: "Randomly select items from your custom lists or shuffle list orders",
          icon: ListOrdered 
        },
        { 
          name: "Drawing Tools", 
          path: "/drawings", 
          description: "Random drawing and selection tools for contests and giveaways",
          icon: Calculator 
        },
      ]
    },
  ];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            RandomGen Number Generator Tools Sitemap
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Complete directory of all random number generation tools and features available on RandomGen
          </p>
        </header>

        <div className="space-y-8">
          {numberTools.map((section) => (
            <Card key={section.category} className="p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-2 text-foreground">
                  {section.category}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {section.description}
                </p>
              </div>
              <nav className="grid gap-4 md:grid-cols-2">
                {section.tools.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <NavLink
                      key={tool.path}
                      to={tool.path}
                      className="group flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-all duration-200"
                    >
                      <Icon className="w-5 h-5 mt-0.5 text-primary group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {tool.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {tool.description}
                        </div>
                      </div>
                    </NavLink>
                  );
                })}
              </nav>
            </Card>
          ))}
        </div>

        <Card className="mt-8 p-6 bg-accent/30">
          <h2 className="text-xl font-semibold mb-3 text-foreground">
            About Our Random Number Generator
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            All our random number generators use cryptographically secure algorithms to ensure true randomness 
            and fairness. Perfect for lottery selections, gaming, statistical sampling, security applications, 
            and any scenario requiring unbiased random number generation.
          </p>
          <div className="flex flex-wrap gap-3">
            <NavLink 
              to="/learn-more" 
              className="text-sm text-primary hover:underline"
            >
              Learn how it works →
            </NavLink>
            <NavLink 
              to="/statistics" 
              className="text-sm text-primary hover:underline"
            >
              View usage statistics →
            </NavLink>
            <NavLink 
              to="/faq" 
              className="text-sm text-primary hover:underline"
            >
              Frequently asked questions →
            </NavLink>
          </div>
        </Card>

        <footer className="mt-12 text-center text-sm text-muted-foreground">
          <p>All tools are free, mobile-optimized, and require no registration</p>
        </footer>
      </div>
    </Layout>
  );
};

export default NumbersGeneratorSitemap;
